/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description Schema for Eligibility form
 */
import _ from 'lodash';
import { FormCollapse } from '@formily/antd-v5';

import { getDecimalInput } from 'utils/workflow';
import { disableFutureDates, getProviderIdentifiersById } from 'utils/eligibility/eligibilityRequestForm';
import { FIELD_VALIDATORS, YEAR_MONTH_DATE_FORMAT } from 'app-constants/index';
import {
	ELIGIBILITY_GENDER_OPTIONS,
	ELIGIBILITY_PATIENT_DEFAULT_RELATIONSHIP,
	ELIGIBILITY_PATIENT_RELATIONSHIP_OPTIONS,
} from 'app-constants/eligibility';
import { CLAIM_STATUS_FORM_FIELDS } from 'app-constants/claims';
import {
	createFormFieldRow,
	patientSectionPath,
	providerIdentifierKeys,
	updateProviderSection,
	validateServiceEndDate,
	validateServiceStartDate,
} from './eligibilityRequestFormSchema';
import { ANT_FORMILY_FORM_WRAPPER_CLASS } from 'app-constants/antd';

/**
 * schema keys
 */
const schemaKeys: Record<keyof ClaimStatusRequestForm.IFormModel, string> = {
	...providerIdentifierKeys,
	payerId: 'payerId',
	providerId: 'providerId',
	locationId: 'locationId',
	memberIdOfPatient: 'memberIdOfPatient',
	accountNumberOfPatient: 'accountNumberOfPatient',
	firstNameOfPatient: 'firstNameOfPatient',
	lastNameOfPatient: 'lastNameOfPatient',
	dateOfBirthOfPatient: 'dateOfBirthOfPatient',
	serviceStartDate: 'serviceStartDate',
	serviceEndDate: 'serviceEndDate',
	firstNameOfSubscriber: 'firstNameOfSubscriber',
	lastNameOfSubscriber: 'lastNameOfSubscriber',
	claimNumber: 'claimNumber',
	claimAmount: 'claimAmount',
	genderOfPatient: 'genderOfPatient',
	relationWithSubscriber: 'relationWithSubscriber',
	patientReqFields: 'patientReqFields',
	providerReqFields: 'providerReqFields',
};

// mapping of schemakeys and payer fields
const requiredFieldsMapping: Record<keyof ClaimStatusRequestForm.IFormModel, string> = {
	...providerIdentifierKeys,
	providerId: 'providerId',
	payerId: 'payerId',
	locationId: 'locationId',
	memberIdOfPatient: 'memberId',
	accountNumberOfPatient: 'accountNumber',
	firstNameOfPatient: 'firstName',
	lastNameOfPatient: 'lastName',
	dateOfBirthOfPatient: 'birthDate',
	genderOfPatient: 'genderCode',
	patientReqFields: 'patientReqFields',
	serviceStartDate: 'fromDate',
	serviceEndDate: 'toDate',
	firstNameOfSubscriber: 'subscriberFirstName',
	lastNameOfSubscriber: 'subscribeLastName',
	claimNumber: 'claimNumber',
	claimAmount: 'claimAmount',
	relationWithSubscriber: 'subscriberRelationship',
	providerReqFields: 'providerReqFields',
};

/**
 * form fields to translate
 */
const formFieldsToTranslate: string[] = [
	..._.values(providerIdentifierKeys),
	schemaKeys.memberIdOfPatient,
	schemaKeys.accountNumberOfPatient,
	schemaKeys.firstNameOfPatient,
	schemaKeys.lastNameOfPatient,
	schemaKeys.firstNameOfSubscriber,
	schemaKeys.lastNameOfSubscriber,
	schemaKeys.claimNumber,
];

// fields config mapping
const fieldsConfigMapping: Record<keyof ClaimStatusRequestForm.IFormModel, string> = _.mapValues(requiredFieldsMapping, (value) =>
	_.toLower(value),
);

const formSections = {
	provider: {
		key: 'provider',
		title: 'Provider/Organization Details',
	},
	payer: {
		key: 'payer',
		title: 'Payer Details',
	},
	patientDetails: {
		key: 'patientDetails',
		title: 'Patient Details',
	},
	subscriberDetails: {
		key: 'subscriberDetails',
		title: 'Subscriber Details',
	},
	claimInformation: {
		key: 'claimInformation',
		title: 'Encounter Details',
	},
};

/**
 * index order for patient required fields
 */
const patientFieldsIndex = {
	patientReqFields: 0,
	memberIdOfPatient: 1,
	firstNameOfPatient: 2,
	lastNameOfPatient: 3,
	dateOfBirthOfPatient: 4,
	genderOfPatient: 5,
	accountNumberOfPatient: 6,
};

/**
 * get required fields for schema
 */
const getRequiredFields = (fieldsCombination?: string): Record<keyof ClaimStatusRequestForm.IFormModel, boolean> => {
	const requiredFieldsForPayer = JSON.parse(fieldsCombination || JSON.stringify('')) as string[];
	const defaultProviderIdentifierMapping = _.mapValues(providerIdentifierKeys, () => false);
	const defaultMapping: Record<keyof ClaimStatusRequestForm.IFormModel, boolean> = {
		...defaultProviderIdentifierMapping,
		providerId: true,
		payerId: true,
		locationId: false,
		memberIdOfPatient: true,
		accountNumberOfPatient: true,
		firstNameOfPatient: true,
		lastNameOfPatient: true,
		dateOfBirthOfPatient: true,
		serviceStartDate: true,
		serviceEndDate: true,
		relationWithSubscriber: false,
		firstNameOfSubscriber: true,
		lastNameOfSubscriber: true,
		claimNumber: true,
		claimAmount: true,
		genderOfPatient: false,
		patientReqFields: false,
		providerReqFields: false,
	};

	if (!_.isEmpty(requiredFieldsForPayer)) {
		requiredFieldsForPayer.push(requiredFieldsMapping.providerId);
		requiredFieldsForPayer.push(requiredFieldsMapping.payerId);
		requiredFieldsForPayer.push(requiredFieldsMapping.serviceStartDate);
		// temporary hard code fields for hotfix
		requiredFieldsForPayer.push(requiredFieldsMapping.memberIdOfPatient);
		requiredFieldsForPayer.push(requiredFieldsMapping.firstNameOfPatient);
		requiredFieldsForPayer.push(requiredFieldsMapping.lastNameOfPatient);
		requiredFieldsForPayer.push(requiredFieldsMapping.dateOfBirthOfPatient);
		return _.mapValues(defaultMapping, (__, field) => {
			return _.includes(requiredFieldsForPayer, _.get(requiredFieldsMapping, field));
		});
	}
	//
	return defaultMapping;
};

const getRequiredFieldsCombination = (combinations?: string[][]): EligibilityRequestForm.IOptionProps[] => {
	let options = [] as EligibilityRequestForm.IOptionProps[];
	const invertedRequiredFieldsMapping = _.invert(requiredFieldsMapping);
	const getFieldLabel = (key?: string) => _.get(CLAIM_STATUS_FORM_FIELDS, key || '')?.title;
	if (combinations) {
		options = _.map(combinations, (combination) => ({
			label: _.join(
				_.map(combination, (word) => getFieldLabel(_.get(invertedRequiredFieldsMapping, word)) || word),
				', ',
			),
			value: JSON.stringify(combination),
		}));
	}
	return options;
};

const getPatientKeys = (claimStatusMeta: Payers.IClaimStatusMeta) => {
	const memberIdTooltip = _.get(claimStatusMeta?.fieldNotes, fieldsConfigMapping.memberIdOfPatient);
	return {
		[schemaKeys.memberIdOfPatient]: {
			type: 'string',
			title: CLAIM_STATUS_FORM_FIELDS.memberIdOfPatient.title,
			'x-component': 'TooltipWrapper',
			'x-component-props': {
				component: 'Input',
				tooltipText: memberIdTooltip,
				onInput: (e: React.ChangeEvent<HTMLInputElement>) => {
					e.target.value = _.trim(_.toUpper(e.target.value));
				},
				placeholder: CLAIM_STATUS_FORM_FIELDS.memberIdOfPatient.placeholder,
			},
			'x-decorator': 'FormItem',
			'x-validator': [FIELD_VALIDATORS.memberId],
		},
		[schemaKeys.genderOfPatient]: {
			type: 'string',
			title: CLAIM_STATUS_FORM_FIELDS.genderOfPatient.title,
			'x-component': 'Select',
			'x-decorator': 'FormItem',
			'x-component-props': {
				options: ELIGIBILITY_GENDER_OPTIONS,
				placeholder: CLAIM_STATUS_FORM_FIELDS.genderOfPatient.placeholder,
				allowClear: true,
			},
		},
		[schemaKeys.accountNumberOfPatient]: {
			type: 'string',
			title: CLAIM_STATUS_FORM_FIELDS.accountNumberOfPatient.title,
			'x-component': 'Input',
			'x-component-props': {
				onInput: (e: React.ChangeEvent<HTMLInputElement>) => {
					e.target.value = _.trim(_.toUpper(e.target.value));
				},
				placeholder: CLAIM_STATUS_FORM_FIELDS.accountNumberOfPatient.placeholder,
			},
			'x-decorator': 'FormItem',
		},
	};
};

const updatePatientSection = (
	schema: any,
	requiredFields: Record<keyof ClaimStatusRequestForm.IFormModel, boolean>,
	claimStatusMeta: Payers.IClaimStatusMeta,
): void => {
	const patientKeys = getPatientKeys(claimStatusMeta);
	const requiredFieldsItem = _.pick(_.cloneDeep(patientKeys), _.keys(_.pickBy(requiredFields, _.identity))) as Record<string, any>;
	_.forEach(requiredFieldsItem, (item, key) => {
		requiredFieldsItem[key] = {
			...item,
			'x-validator': [
				..._.get(item, 'x-validator', []),
				{
					required: true,
					message: _.get(CLAIM_STATUS_FORM_FIELDS, key).errorMessage,
				},
			],
			'x-index': _.get(patientFieldsIndex, key),
		};
	});

	const nonRequiredFieldsItem = _.omit(_.cloneDeep(patientKeys), _.keys(requiredFieldsItem)) as Record<string, any>;

	const patientSection = _.omit(_.get(schema, patientSectionPath), _.keys(patientKeys));
	const extraFields = _.omit(patientSection, _.keys(schemaKeys));
	const reqPatientFields = { ..._.omit(patientSection, [..._.keys(extraFields), schemaKeys.patientReqFields]), ...requiredFieldsItem };
	const searchReqFieldRow = createFormFieldRow(
		'patientReqFieldRow',
		{
			[schemaKeys.patientReqFields]: patientSection?.[schemaKeys.patientReqFields],
		},
		0,
	);
	const reqFieldRows = createFormFieldRow('reqFieldRows', reqPatientFields, 1);
	_.set(schema, patientSectionPath, { ...searchReqFieldRow, ...reqFieldRows, ...extraFields });
	const collapsePath = _.concat(patientSectionPath, ['nonRequiredFields', 'properties', 'firstCollapse', 'properties']);
	const nonRequiredFieldsSection = createFormFieldRow('nonRequiredFields', nonRequiredFieldsItem);
	_.set(schema, collapsePath, { ...nonRequiredFieldsSection });
};

const generateSchema = ({
	metadata,
	selectedReqFields,
	relationWithSubscriber,
	isOpenCollapsed,
	payerData,
	formValues,
}: {
	metadata: Metadata.IResponse;
	selectedReqFields: EligibilityRequestForm.IRequiredFieldsGrouping;
	relationWithSubscriber: string;
	isOpenCollapsed?: boolean;
	payerData: Payers.IPayerMeta;
	formValues: ClaimStatusRequestForm.IFormModel;
}): IFormilySchema => {
	const formCollapse = FormCollapse.createFormCollapse();
	const claimStatusMeta = payerData?.payerMeta?.claimStatusMeta;
	const currentProviderPayerMeta = getProviderIdentifiersById(
		_.get(formValues, schemaKeys.providerId) || '',
		payerData?.providerPayerMeta?.data,
	);
	const patientFieldCombinations = claimStatusMeta?.requiredFieldCombinations?.patient;
	const providerFieldCombinations = claimStatusMeta?.requiredFieldCombinations?.provider;
	const claimFieldCombinations = claimStatusMeta?.requiredFieldCombinations?.Claim;
	const encounterFieldCombinations = claimStatusMeta?.requiredFieldCombinations?.encounter;
	const patientRequiredFieldsCombination = getRequiredFieldsCombination(patientFieldCombinations);
	const providerRequiredFieldsCombination = getRequiredFieldsCombination(providerFieldCombinations);
	const claimRequiredFieldsCombination = getRequiredFieldsCombination(claimFieldCombinations);
	const encounterRequiredFieldsCombination = getRequiredFieldsCombination(encounterFieldCombinations);
	const requiredFields = getRequiredFields(
		JSON.stringify([
			...JSON.parse((selectedReqFields?.patient || _.first(patientRequiredFieldsCombination)?.value) ?? JSON.stringify('')),
			...JSON.parse((selectedReqFields?.provider || _.first(providerRequiredFieldsCombination)?.value) ?? JSON.stringify('')),
			...JSON.parse(_.first(claimRequiredFieldsCombination)?.value ?? JSON.stringify('')),
			...JSON.parse(_.first(encounterRequiredFieldsCombination)?.value ?? JSON.stringify('')),
		]),
	);
	const isRequiredDob =
		requiredFields.dateOfBirthOfPatient ||
		(!!relationWithSubscriber && relationWithSubscriber !== ELIGIBILITY_PATIENT_DEFAULT_RELATIONSHIP);

	const schema = {
		title: 'Claim Status Form',
		type: 'object',
		properties: {
			formWrapper: {
				type: 'void',
				'x-component': 'Space',
				'x-component-props': {
					direction: 'vertical',
					size: 20,
					className: ANT_FORMILY_FORM_WRAPPER_CLASS,
				},
				properties: {
					[formSections.payer.key]: {
						type: 'void',
						'x-component': 'Card',
						'x-component-props': {
							title: formSections.payer.title,
						},
						properties: {
							fieldsWrapper: {
								type: 'void',
								'x-component': 'div',
								properties: createFormFieldRow('payerFieldRow', {
									[schemaKeys.payerId]: {
										type: 'string',
										title: CLAIM_STATUS_FORM_FIELDS.payerId.title,
										'x-component': 'SelectedPayers',
										'x-component-props': {
											placeholder: CLAIM_STATUS_FORM_FIELDS.payerId.placeholder,
											forClaimStatus: true,
											selectedPayers: metadata.selectedPayers,
										},
										'x-decorator': 'FormItem',
										'x-decorator-props': { tooltip: CLAIM_STATUS_FORM_FIELDS.payerId.info },
										'x-validator': [
											{
												required: requiredFields.payerId,
												message: CLAIM_STATUS_FORM_FIELDS.payerId.errorMessage,
											},
										],
									},
								}),
							},
						},
					},
					[formSections.provider.key]: {
						type: 'void',
						'x-component': 'Card',
						'x-component-props': {
							title: formSections.provider.title,
						},
						properties: {
							fieldsWrapper: {
								type: 'void',
								'x-component': 'div',
								properties: {
									[schemaKeys.providerReqFields]: {
										type: 'string',
										title: CLAIM_STATUS_FORM_FIELDS.providerReqFields.title,
										'x-hidden': _.size(providerRequiredFieldsCombination) <= 1,
										'x-index': 1,
										'x-component': 'Select',
										'x-decorator': 'FormItem',
										'x-decorator-props': { labelStyle: { fontStyle: 'italic' } },
										'x-component-props': {
											options: providerRequiredFieldsCombination,
											placeholder: CLAIM_STATUS_FORM_FIELDS.providerReqFields.placeholder,
										},
									},
									[schemaKeys.providerId]: {
										type: 'string',
										title: CLAIM_STATUS_FORM_FIELDS.providerId.title,
										'x-index': 2,
										'x-component': 'ProviderNpis',
										'x-component-props': {
											metadata,
											payersMeta: payerData,
											placeholder: CLAIM_STATUS_FORM_FIELDS.providerId.placeholder,
										},
										'x-decorator': 'FormItem',
										'x-decorator-props': { tooltip: CLAIM_STATUS_FORM_FIELDS.providerId.info },
										'x-validator': [
											{
												required: requiredFields.providerId,
												message: CLAIM_STATUS_FORM_FIELDS.providerId.errorMessage,
											},
										],
									},
								},
							},
						},
					},
					[formSections.patientDetails.key]: {
						type: 'void',
						'x-component': 'Card',
						'x-component-props': {
							title: formSections.patientDetails.title,
						},
						properties: {
							fieldsWrapper: {
								type: 'void',
								'x-component': 'div',
								'x-component-props': {
									className: 'patient-section',
								},
								properties: {
									[schemaKeys.patientReqFields]: {
										type: 'string',
										title: CLAIM_STATUS_FORM_FIELDS.patientReqFields.title,
										'x-hidden': _.size(patientRequiredFieldsCombination) <= 1,
										'x-index': patientFieldsIndex.patientReqFields,
										'x-component': 'Select',
										'x-decorator': 'FormItem',
										'x-decorator-props': { labelStyle: { fontStyle: 'italic' } },
										'x-component-props': {
											options: patientRequiredFieldsCombination,
											placeholder: CLAIM_STATUS_FORM_FIELDS.patientReqFields.placeholder,
										},
									},
									[schemaKeys.firstNameOfPatient]: {
										type: 'string',
										title: CLAIM_STATUS_FORM_FIELDS.firstNameOfPatient.title,
										'x-component': 'Input',
										'x-component-props': {
											placeholder: CLAIM_STATUS_FORM_FIELDS.firstNameOfPatient.placeholder,
										},
										'x-decorator': 'FormItem',
										'x-index': requiredFields.firstNameOfPatient ? patientFieldsIndex.firstNameOfPatient : undefined,
										'x-validator': [
											{
												required: requiredFields.firstNameOfPatient,
												message: CLAIM_STATUS_FORM_FIELDS.firstNameOfPatient.errorMessage,
											},
											FIELD_VALIDATORS.patientFirstName,
										],
									},
									[schemaKeys.lastNameOfPatient]: {
										type: 'string',
										title: CLAIM_STATUS_FORM_FIELDS.lastNameOfPatient.title,
										'x-component': 'Input',
										'x-component-props': {
											placeholder: CLAIM_STATUS_FORM_FIELDS.lastNameOfPatient.placeholder,
										},
										'x-decorator': 'FormItem',
										'x-index': requiredFields.lastNameOfPatient ? patientFieldsIndex.lastNameOfPatient : undefined,
										'x-validator': [
											{
												required: requiredFields.lastNameOfPatient,
												message: CLAIM_STATUS_FORM_FIELDS.lastNameOfPatient.errorMessage,
											},
											FIELD_VALIDATORS.patientLastName,
										],
									},

									[schemaKeys.dateOfBirthOfPatient]: {
										type: 'string',
										title: CLAIM_STATUS_FORM_FIELDS.dateOfBirthOfPatient.title,
										'x-component': 'DatePicker',
										'x-component-props': {
											placeholder: CLAIM_STATUS_FORM_FIELDS.dateOfBirthOfPatient.placeholder,
											disabledDate: disableFutureDates,
											inputDateFormat: YEAR_MONTH_DATE_FORMAT,
											allowClear: true,
										},
										'x-decorator': 'FormItem',
										'x-index': isRequiredDob ? patientFieldsIndex.dateOfBirthOfPatient : undefined,
										'x-validator': [
											{
												required: requiredFields.dateOfBirthOfPatient,
												message: CLAIM_STATUS_FORM_FIELDS.dateOfBirthOfPatient.errorMessage,
											},
										],
									},
									[schemaKeys.relationWithSubscriber]: {
										type: 'string',
										'x-component': 'Select',
										'x-decorator': 'FormItem',
										title: CLAIM_STATUS_FORM_FIELDS.relationWithSubscriber.title,
										'x-component-props': {
											options: ELIGIBILITY_PATIENT_RELATIONSHIP_OPTIONS,
											placeholder: CLAIM_STATUS_FORM_FIELDS.relationWithSubscriber.placeholder,
										},
										'x-validator': [
											{
												required: requiredFields.relationWithSubscriber,
												message: CLAIM_STATUS_FORM_FIELDS.relationWithSubscriber.errorMessage,
											},
										],
									},

									nonRequiredFields: {
										type: 'void',
										'x-component': 'FormCollapse',
										'x-component-props': {
											...formCollapse,
											defaultActiveKey: isOpenCollapsed ? ['firstCollapse'] : [],
										},
										properties: {
											firstCollapse: {
												type: 'void',
												'x-component': 'FormCollapse.CollapsePanel',
												'x-component-props': {
													header: 'Additional Fields',
												},
												properties: {},
											},
										},
									},
								},
							},
						},
					},
					[formSections.subscriberDetails.key]: {
						type: 'void',
						'x-component': 'Card',
						'x-component-props': {
							title: formSections.subscriberDetails.title,
						},
						'x-reactions': [
							{
								dependencies: [schemaKeys.relationWithSubscriber],
								fulfill: {
									schema: {
										'x-visible': `{{$deps[0] !=='${ELIGIBILITY_PATIENT_DEFAULT_RELATIONSHIP}'}}`,
									},
								},
							},
						],
						properties: {
							fieldsWrapper: {
								type: 'void',
								'x-component': 'div',
								properties: createFormFieldRow('subscriberFieldRow', {
									[schemaKeys.firstNameOfSubscriber]: {
										type: 'string',
										title: CLAIM_STATUS_FORM_FIELDS.firstNameOfSubscriber.title,
										'x-component': 'Input',
										'x-component-props': {
											placeholder: CLAIM_STATUS_FORM_FIELDS.firstNameOfSubscriber.placeholder,
										},
										'x-decorator': 'FormItem',
										// todo if required uncomment out
										// 'x-validator': [
										// 	{
										// 		required: requiredFields.firstNameOfSubscriber,
										// 		message: CLAIM_STATUS_FORM_FIELDS.firstNameOfSubscriber.errorMessage,
										// 	},
										// ],
									},
									[schemaKeys.lastNameOfSubscriber]: {
										type: 'string',
										title: CLAIM_STATUS_FORM_FIELDS.lastNameOfSubscriber.title,
										'x-component': 'Input',
										'x-component-props': {
											placeholder: CLAIM_STATUS_FORM_FIELDS.lastNameOfSubscriber.placeholder,
										},
										'x-decorator': 'FormItem',
										'x-validator': [
											{
												required: true,
												message: CLAIM_STATUS_FORM_FIELDS.lastNameOfSubscriber.errorMessage,
											},
										],
									},
								}),
							},
						},
					},
					[formSections.claimInformation.key]: {
						type: 'void',
						'x-component': 'Card',
						'x-component-props': {
							title: formSections.claimInformation.title,
						},
						properties: {
							fieldsWrapper: {
								type: 'void',
								'x-component': 'div',
								properties: createFormFieldRow('serviceFieldRow', {
									[schemaKeys.serviceStartDate]: {
										type: 'string',
										title: CLAIM_STATUS_FORM_FIELDS.serviceStartDate.title,
										'x-component': 'TooltipWrapper',
										'x-component-props': {
											component: 'DatePicker',
											placeholder: CLAIM_STATUS_FORM_FIELDS.serviceStartDate.placeholder,
											inputDateFormat: YEAR_MONTH_DATE_FORMAT,
											allowClear: false,
										},
										'x-decorator': 'FormItem',
										'x-decorator-props': { tooltip: CLAIM_STATUS_FORM_FIELDS.serviceStartDate.info },
										'x-validator': [
											{
												required: requiredFields.serviceStartDate,
												message: CLAIM_STATUS_FORM_FIELDS.serviceStartDate.errorMessage,
											},
											{
												validator: (value: string, _rule: any, ctx: any) => {
													const endDate = ctx?.form?.values?.serviceEndDate;
													if (!value) {
														return '';
													}
													return validateServiceStartDate(value, endDate);
												},
											},
										],
										'x-reactions': [
											{
												dependencies: [schemaKeys.serviceStartDate],
												fulfill: {
													run: "$form.validate('serviceEndDate').catch(() => {})",
												},
											},
										],
									},
									[schemaKeys.serviceEndDate]: {
										type: 'string',
										title: CLAIM_STATUS_FORM_FIELDS.serviceEndDate.title,
										'x-component': 'TooltipWrapper',
										'x-component-props': {
											component: 'DatePicker',
											placeholder: CLAIM_STATUS_FORM_FIELDS.serviceEndDate.placeholder,
											inputDateFormat: YEAR_MONTH_DATE_FORMAT,
										},
										'x-decorator': 'FormItem',
										'x-decorator-props': { tooltip: CLAIM_STATUS_FORM_FIELDS.serviceEndDate.info },

										'x-validator': [
											{
												required: requiredFields.serviceEndDate,
												message: CLAIM_STATUS_FORM_FIELDS.serviceEndDate.errorMessage,
											},
											{
												validator: (value: string, _rule: any, ctx: any) => {
													const startDate = ctx?.form?.values?.serviceStartDate;
													if (!value || !startDate) {
														return '';
													}
													return validateServiceEndDate(value, startDate);
												},
											},
										],
										'x-reactions': [
											{
												dependencies: [schemaKeys.serviceEndDate],
												fulfill: {
													run: "$form.validate('serviceStartDate').catch(() => {})",
												},
											},
										],
									},
									[schemaKeys.claimAmount]: {
										type: 'string',
										title: CLAIM_STATUS_FORM_FIELDS.claimAmount.title,
										'x-component': 'Input',
										'x-component-props': {
											onInput: (e: React.ChangeEvent<HTMLInputElement>) => {
												e.target.value = getDecimalInput(e.target.value);
											},
											placeholder: CLAIM_STATUS_FORM_FIELDS.claimAmount.placeholder,
										},
										'x-decorator': 'FormItem',
										'x-decorator-props': { tooltip: CLAIM_STATUS_FORM_FIELDS.claimAmount.info },
										'x-validator': [
											{
												required: requiredFields.claimAmount,
												message: CLAIM_STATUS_FORM_FIELDS.claimAmount.errorMessage,
											},
										],
									},
									[schemaKeys.claimNumber]: {
										type: 'string',
										title: CLAIM_STATUS_FORM_FIELDS.claimNumber.title,
										'x-component': 'Input',
										'x-component-props': {
											placeholder: CLAIM_STATUS_FORM_FIELDS.claimNumber.placeholder,
										},
										'x-decorator': 'FormItem',
										'x-decorator-props': { tooltip: CLAIM_STATUS_FORM_FIELDS.claimNumber.info },
										'x-validator': [
											{
												required: requiredFields.claimNumber,
												message: CLAIM_STATUS_FORM_FIELDS.claimNumber.errorMessage,
											},
										],
									},
									[schemaKeys.locationId]: {
										type: 'string',
										title: CLAIM_STATUS_FORM_FIELDS.locationId.title,
										'x-component': 'Select',
										'x-component-props': {
											placeholder: CLAIM_STATUS_FORM_FIELDS.locationId.placeholder,
											options: metadata?.locationGroupOptions,
											allowClear: true,
										},
										'x-decorator': 'FormItem',
										'x-validator': [
											{
												required: requiredFields.locationId,
												message: CLAIM_STATUS_FORM_FIELDS.locationId.errorMessage,
											},
										],
									},
								}),
							},
						},
					},
				},
			},
		},
	};
	updatePatientSection(schema, requiredFields, claimStatusMeta);
	updateProviderSection(schema, requiredFields, currentProviderPayerMeta);
	return schema;
};

export {
	schemaKeys,
	fieldsConfigMapping,
	requiredFieldsMapping,
	patientFieldsIndex,
	formFieldsToTranslate,
	generateSchema,
	getRequiredFields,
	updatePatientSection,
	getRequiredFieldsCombination,
};
